/**
 * Helper utilities for page load tracking
 * These functions extract data from sessionStorage for Adobe Analytics tracking
 */

import { checkIsExternal, checkIsInternal, checkContractIdFlow } from "./createStorage";

export function getCurrentFlow() {
  if (checkIsExternal()) return 'EXTERNAL';
  if (checkContractIdFlow()) return 'CONTRACT_ID';
  if (checkSingleTin()) return 'SINGLE_TIN';
  if (checkMultiTin()) return 'MULTI_TIN';
  if (checkGeoFlow()) return 'GEO_FLOW';
  if (checkHCO()) return 'HCO';
  return 'UNKNOWN_FLOW';
}

export function checkSingleTin() {
  const multiSelectedTins = sessionStorage.getItem('multiSelectedTins');
  return sessionStorage.getItem('multiTinNavigation') &&
    multiSelectedTins && multiSelectedTins.split(',').length === 1;
}
export function checkMultiTin() {
  const multiSelectedTins = sessionStorage.getItem('multiSelectedTins');
  return sessionStorage.getItem('multiTinNavigation') &&
    multiSelectedTins && multiSelectedTins.split(',').length > 1;
}
export function checkGeoFlow() {
  const geoCriteria = sessionStorage.getItem('GeoCriteria');
  return !!geoCriteria && geoCriteria !== 'null';
}
export function getGeoCriteria() {
  const geoCriteria = sessionStorage.getItem('GeoCriteria');
  return geoCriteria && geoCriteria !== 'null' ? geoCriteria : null;
}
export function checkHCO() {
  return !sessionStorage.getItem('multiTinNavigation');
}

/**
 * Helper function to safely parse JSON from sessionStorage
 * @param key - The sessionStorage key to retrieve
 * @returns Parsed JSON object or null if not found/invalid
 */
export function getSessionStorageItem<T = any>(key: string): T | null {
  try {
    const item = sessionStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch {
    return null;
  }
}

/**
 * Parse URL and create dynamic siteSectionL levels
 * @param url - The URL to parse (optional, defaults to current URL)
 * @returns Object with siteSectionL1, siteSectionL2, etc. and pageName
 */
export function parseUrlForSiteSections(url?: string) {
  const currentUrl = url || window.location.pathname;
  const splittedURL = currentUrl.split('/').filter(element => element);

  const sections: Record<string, string> = {};
  let pageName = 'uhcinsights';

  splittedURL.forEach((element, index) => {
    const cleanElement = element.split('?')[0]; // Remove query params
    const siteSectionKey = `siteSectionL${index + 1}`;
    sections[siteSectionKey] = cleanElement;
    pageName += ':' + cleanElement.toLowerCase();
  });

  return { sections, pageName };
}

/**
 * Get formatted component name from URL for tracking purposes
 * Converts URL path to a readable component name (e.g., /PSMS/PriorAuthorization -> "PSMS - Prior Authorization")
 * @param url - The URL to parse (optional, defaults to current URL)
 * @returns Formatted component name string
 */
export function getComponentNameFromUrl(url?: string): string {
  const currentUrl = url || window.location.pathname;
  const pathSegments = currentUrl.split('/').filter(element => element && element.trim());

  if (pathSegments.length === 0) {
    return 'Home';
  }

  // Convert path segments to title case and join with ' - '
  const formattedSegments = pathSegments.map(segment => {
    // Remove query params and decode URI
    const cleanSegment = decodeURIComponent(segment.split('?')[0]);

    // Convert camelCase or PascalCase to spaces: "PriorAuthorization" -> "Prior Authorization"
    const withSpaces = cleanSegment.replace(/([a-z])([A-Z])/g, '$1 $2');

    // Capitalize each word
    return withSpaces
      .split(/[\s_-]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  });

  return formattedSegments.join(' - ');
}

/**
 * Get user data for page load tracking with internal/external detection
 * @returns Object containing msid, userType, providerUuid, and isInternal
 */
export function getUserDataForPageLoad() {
  const currentUserArray = getSessionStorageItem<any[]>('currentUser');
  // currentUser is stored as an array, get the first element
  const userData = Array.isArray(currentUserArray) && currentUserArray.length > 0
    ? currentUserArray[0]
    : null;

  const isInternal = checkIsInternal();

  // Extract UserType from UserPersonas if available
  const userType = userData?.UserPersonas?.[0]?.UserType?.toLowerCase() ||
    (isInternal ? 'internal' : 'external');

  // Use oid as UUID for external users
  const providerUuid = isInternal ? '' : (userData?.oid || userData?.uuid || '');

  return {
    msid: isInternal ? (userData?.MsId || '') : '',
    userType,
    providerUuid,
    isInternal,
    // Additional user info that might be useful
    firstName: userData?.FirstName || '',
    lastName: userData?.LastName || '',
    emailId: userData?.EmailId || '',
    healthCareOrganizationName: userData?.HealthCareOrganizationName || '',
    providerKey: userData?.ProviderKey || ''
  };
}

/**
 * Get provider and TIN data with advanced logic for internal/external users
 * Handles simulation mode, multi-TIN navigation, and various scenarios
 * @returns Object containing corporateProviderName, providerTaxIdNumber, providerTaxId
 */
export function getProviderDataForPageLoad() {
  const userData = getUserDataForPageLoad();
  const isInternal = userData.isInternal;

  let corporateProviderName = userData.healthCareOrganizationName || '';
  let providerTaxIdNumber = '';
  let providerUuid = userData.providerUuid;

  // Check if current URL contains PSMS to determine which provider info to use
  const isPSMSPage = window.location.pathname.toLowerCase().includes('psms');
  const providerInfo = getSessionStorageItem(isPSMSPage ? 'providerPSMSInfo' : 'providerInfo');

  if (providerInfo) {
    corporateProviderName = providerInfo?.ProviderEntityName || providerInfo?.ProviderSystem || corporateProviderName;
    providerUuid = providerInfo?.ProviderEntityID || providerUuid;
  }

  // Handle external users
  if (!isInternal) {
    const topFilterTin = getSessionStorageItem('topFilterTin');
    corporateProviderName = topFilterTin?.name || corporateProviderName;
    providerTaxIdNumber = topFilterTin?.value || '';
  }

  // Handle internal users with different scenarios
  if (isInternal) {
    const selectedTin = getSessionStorageItem('selectedTin');
    const simulationMode = getSessionStorageItem('simulationMode');
    const multiTinNavigation = sessionStorage.getItem('multiTinNavigation');
    const multiSelectedTins = sessionStorage.getItem('multiSelectedTins');

    // Simulation mode
    if (simulationMode) {
      providerTaxIdNumber = selectedTin?.value || '';
      corporateProviderName = selectedTin?.name || corporateProviderName;
    }
    // Multi-TIN navigation - single TIN
    else if (multiTinNavigation && multiSelectedTins && multiSelectedTins.split(',').length === 1) {
      providerTaxIdNumber = selectedTin?.value || '';
      corporateProviderName = selectedTin?.name || corporateProviderName;
    }
    // Multi-TIN navigation - multiple TINs
    else if (multiTinNavigation && multiSelectedTins && multiSelectedTins.split(',').length > 1) {
      providerTaxIdNumber = multiSelectedTins;
      corporateProviderName = sessionStorage.getItem('groupName') || corporateProviderName;
    }
  }

  // Get TIN from state if available (fallback)
  const sessionState = getSessionStorageItem('state');
  let providerTaxId = '';
  if (sessionState?.taxId && Array.isArray(sessionState.taxId)) {
    const tinList = sessionState.taxId.map((item: any) => item.Tin || item.value).filter(Boolean);
    providerTaxId = tinList.join(',');
  }

  // Fallback: if providerTaxIdNumber is empty, use multiTinValues
  if (!providerTaxIdNumber) {
    const tinArray = getSessionStorageItem<string[]>('multiTinValues');
    if (Array.isArray(tinArray) && tinArray.length > 0) {
      providerTaxIdNumber = tinArray.join(',');
    }
  }

  // Use providerTaxId as final fallback
  if (!providerTaxIdNumber && providerTaxId) {
    providerTaxIdNumber = providerTaxId;
  }

  return {
    corporateProviderName,
    providerTaxIdNumber,
    providerTaxId: providerTaxId || providerTaxIdNumber,
    providerUuid
  };
}

/**
 * Get Tax ID information for page load tracking
 * @deprecated Use getProviderDataForPageLoad instead for comprehensive data
 * @returns Object containing providerTaxIdNumber and providerTaxId
 */
export function getTaxIdDataForPageLoad() {
  const tinArray = getSessionStorageItem<string[]>('multiTinValues');
  if (Array.isArray(tinArray) && tinArray.length > 0) {
    return {
      providerTaxIdNumber: tinArray.join(','),
      providerTaxId: tinArray.length > 1 ? 'All' : (tinArray[0] || 'All')
    };
  }
  return {
    providerTaxIdNumber: '',
    providerTaxId: 'All'
  };
}
